/**
 * useViewportHeight - Hook for handling mobile keyboard viewport issues
 * 
 * On mobile, 100vh includes the space behind the virtual keyboard,
 * causing content to be hidden when the keyboard opens. This hook uses
 * the Visual Viewport API to track the actual visible viewport height.
 */

import { useState, useEffect } from 'react';

interface ViewportDimensions {
  height: number;
  isKeyboardOpen: boolean;
}

export const useViewportHeight = (): ViewportDimensions => {
  const [dimensions, setDimensions] = useState<ViewportDimensions>(() => ({
    height: typeof window !== 'undefined' 
      ? window.visualViewport?.height ?? window.innerHeight 
      : 0,
    isKeyboardOpen: false
  }));

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const viewport = window.visualViewport;
    
    // Store initial height to detect keyboard
    const initialHeight = window.innerHeight;
    
    const updateHeight = () => {
      const currentHeight = viewport?.height ?? window.innerHeight;
      // Consider keyboard open if viewport shrunk by more than 150px
      const isKeyboardOpen = initialHeight - currentHeight > 150;
      
      setDimensions({
        height: currentHeight,
        isKeyboardOpen
      });
    };

    // Initial update
    updateHeight();

    // Listen to visualViewport events (best for keyboard detection)
    if (viewport) {
      viewport.addEventListener('resize', updateHeight);
      viewport.addEventListener('scroll', updateHeight);
    }
    
    // Fallback for browsers without visualViewport
    window.addEventListener('resize', updateHeight);

    return () => {
      if (viewport) {
        viewport.removeEventListener('resize', updateHeight);
        viewport.removeEventListener('scroll', updateHeight);
      }
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return dimensions;
};

export default useViewportHeight;

